/**
 * @typedef {Object} ReplaceableElement
 * @property {Element} element - The dom element
 * @property {() => void} init - Initializes the current element
 * @property {() => void} startLoading - Starts loading overlay on current element
 * @property {() => void} stopLoading - Stops loading overlay on current element
 * @property {(Element) => void} replace - Replaces the current element on the dom
 */

/**
 * @typedef ReplaceableElementInput
 * @property {Element} element - The initial dom element
 * @property {() => void} [init] - Optional initializer function that will be called
 * on every replace call
 */

import {hideLoading, showLoading} from "./loading";

/**
 * Creates a replaceable element from an initial dom element
 * and an initializer function
 * @template {ReplaceableElementInput} T
 * @param {T} inputObject
 * @return T|ReplaceableElement
 */
function createReplaceableElement(inputObject) {
	// Create empty initializer function if missing
	if (!inputObject.init) {
		inputObject.init = function () {
		};
	}

	// Add replace function
	inputObject.replace = function (newElement) {
		const oldElement = this.element;

		// Save vertical scroll position,
		// We trust that the position of the elements will be more or less
		// the same on the DOM tree, so we can restore the vertical scrolling
		// after loading the element back to the DOM
		const scrollHistoryMap = {};
		iterateChildren(oldElement, (element, id) => {
			if (element.scrollTop > 0 || element.scrollLeft) {
				scrollHistoryMap[id] = {
					top: element.scrollTop,
					left: element.scrollLeft,
				};
			}
		})

		// Replace on memory and initialize
		this.element = newElement;
		this.init();

		// Check if previous element was loading this way
		// we can preserve any loading overlay css transitions
		const wasLoading = oldElement.classList.contains('loading');
		if (wasLoading) {
			this.startLoading();
		}

		// Replace on dom
		oldElement.replaceWith(this.element);

		// If element was loading, remove overlay shortly after
		// adding it back to the dom, this is not perfect but it's
		// better than nothing
		if (wasLoading) {
			setTimeout(() => this.stopLoading(), 32);
		}

		// Restore vertical scroll position
		iterateChildren(this.element, (element, id) => {
			if (scrollHistoryMap[id]) {
				element.scrollTop = scrollHistoryMap[id].top;
				element.scrollLeft = scrollHistoryMap[id].left;
			}
		})
	}

	// Add loading helpers
	inputObject.startLoading = function () {
		showLoading(this.element);
	}

	inputObject.stopLoading = function () {
		hideLoading(this.element);
	}

	// Initialize for first time
	inputObject.init();

	return inputObject;

	/**
	 * Methods
	 */

	/**
	 * Iterates recursively through all the children assigning an id
	 * to each child based on its position on the tree
	 *
	 * @param {Element} element
	 * @param {(element: Element,id: string) => void} handleChild
	 * @param id
	 */
	function iterateChildren(element, handleChild, id = '0') {
		handleChild(element, id);

		let index = 0;
		for (const child of element.children) {
			iterateChildren(child, handleChild, `${id}.${index}`);
			index++;
		}
	}
}

export {createReplaceableElement}
